import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useAemConfig } from '@as-react/common-components';
import { Box, ButtonAlpha, Grid, Stack, useBreakpoints, useIsClient } from '@yonderland/andes-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { I18N_NAMESPACES } from '../../../constants/i18nConstants';
import { makeGetProductDefaults, makeGetSelectedColorId, makeGetSelectedSku, } from '../../../redux/product/selectors/ProductSelector';
import { ScrollUtil } from '../../../util/ScrollUtil';
import { ProductMediaCollageItem } from './ProductMediaCollageItem';
import { useProductMediaLightbox } from './ProductMediaLightboxContext';
import { useProductMedia } from './useProductMedia';
const SHOWN_COLLAGE_ITEMS = 4;
const COLLAGE_TEST_ID = 'product_collage';
export const ProductMediaCollage = () => {
    const lightboxContext = useProductMediaLightbox();
    if (!lightboxContext) {
        throw new Error('ProductMediaCollage must be used within ProductMediaLightboxContext.Provider');
    }
    const { popoverHandlers, onActiveSlideIndexChange } = lightboxContext;
    const { t } = useTranslation(I18N_NAMESPACES.product);
    const { isShopLanding } = useAemConfig();
    const { mediaCollection } = useProductMedia();
    const selectedColorId = useSelector(makeGetSelectedColorId());
    const selectedSku = useSelector(makeGetSelectedSku());
    const { productCode, productId } = useSelector(makeGetProductDefaults());
    const { isSm, isMd } = useBreakpoints();
    const [showAllMedia, setShowAllMedia] = useState(false);
    const isClient = useIsClient();
    const wishListProduct = {
        colorId: selectedColorId,
        productCode: productCode,
        productId: parseInt(productId, 10),
        sku: selectedSku,
    };
    const collectionContainsVideos = useMemo(() => mediaCollection.some(m => m.isVideo), [mediaCollection]);
    const getMediaCollectionIndex = (item) => {
        return mediaCollection.findIndex(m => m === item);
    };
    const getVideoButtonPosition = (totalShown) => {
        var _a;
        const positionMap = {
            1: 0, // Single image: first position
            2: 1, // Two images: second position
        };
        return (_a = positionMap[totalShown]) !== null && _a !== void 0 ? _a : 2; // Default: third position for grid layout
    };
    const shouldRenderVideoButton = (index, totalShown) => {
        if (!collectionContainsVideos) {
            return false;
        }
        return index === getVideoButtonPosition(totalShown);
    };
    const getVideoButtonAlignment = (totalShown) => {
        return totalShown >= 3 ? 'start' : 'end';
    };
    const handleClickCollageItem = (event, originalIndex) => {
        onActiveSlideIndexChange(originalIndex);
        popoverHandlers.handlePopoverOpen(event);
    };
    const handlePlayVideo = (event) => {
        const firstVideoIndex = mediaCollection.findIndex(m => m.isVideo);
        if (firstVideoIndex !== -1) {
            onActiveSlideIndexChange(firstVideoIndex);
            popoverHandlers.handlePopoverOpen(event);
        }
    };
    const productCollageItemProps = {
        wishListProduct,
        onClick: handleClickCollageItem,
        onPlayVideoClick: handlePlayVideo,
    };
    const [shownMediaCollection, hiddenMediaCollection] = useMemo(() => {
        const nonVideoMedia = mediaCollection.filter(m => !m.isVideo);
        return [nonVideoMedia.slice(0, SHOWN_COLLAGE_ITEMS), nonVideoMedia.slice(SHOWN_COLLAGE_ITEMS)];
    }, [mediaCollection]);
    const handleClickShowAll = () => {
        setShowAllMedia(prev => !prev);
    };
    return shownMediaCollection.length > 0 ? (_jsx(Box, { paddingBlockEnd: "4", position: { md: 'sticky' }, top: { md: '2' }, children: shownMediaCollection.length === 1 ? (_jsx(Grid, { testId: COLLAGE_TEST_ID, columns: {
                xs: 2,
                lg: 4,
            }, gap: 2, children: _jsx(Grid.Cell, { span: 2, offset: { lg: 2 }, children: _jsx(ProductMediaCollageItem, Object.assign({ index: 0, item: shownMediaCollection[0], showLabels: true, showVideoButton: collectionContainsVideos, videoButtonPosition: getVideoButtonAlignment(shownMediaCollection.length), showWishlistButton: !isShopLanding }, productCollageItemProps)) }) })) : (_jsxs(Grid, { testId: COLLAGE_TEST_ID, columns: {
                xs: 1,
                lg: 2,
            }, gap: 2, children: [shownMediaCollection.map((item, index) => (_jsx(ProductMediaCollageItem, Object.assign({ index: getMediaCollectionIndex(item), item: item, showLabels: index === 0, showVideoButton: shouldRenderVideoButton(index, shownMediaCollection.length), videoButtonPosition: getVideoButtonAlignment(shownMediaCollection.length), showWishlistButton: !isShopLanding && isClient && index === (isSm || isMd ? 0 : 1) }, productCollageItemProps), index))), hiddenMediaCollection.length > 0 &&
                    (!showAllMedia ? (_jsx(Grid.Cell, { span: { lg: 2 }, children: _jsx(Box, { paddingBlockStart: "3", children: _jsx(Stack, { justify: "center", children: _jsx(ButtonAlpha, { testId: "product_collage_show_more", theme: "branded", variant: "secondary", size: "md", text: t('pdp.collage.show.more'), onClick: handleClickShowAll }) }) }) })) : (_jsxs(_Fragment, { children: [hiddenMediaCollection.map((item, index) => (_jsx(ProductMediaCollageItem, Object.assign({ index: getMediaCollectionIndex(item), item: item }, productCollageItemProps), index))), showAllMedia && (_jsx(Grid.Cell, { span: { lg: 2 }, children: _jsx(Box, { paddingBlockStart: "3", children: _jsx(Stack, { justify: "center", children: _jsx(ButtonAlpha, { testId: "product_collage_show_less", theme: "branded", variant: "secondary", size: "md", text: t('pdp.collage.show.less'), onClick: () => {
                                                handleClickShowAll();
                                                ScrollUtil.scrollToTop();
                                            } }) }) }) }))] })))] })) })) : null;
};
